import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useToast } from '../hooks/useToast'
import { useSessionExpiration } from '../hooks/useSessionExpiration'
import { handleGlobalError } from '../utils/errorHandler'
import axios from 'axios'
import { 
  UsersIcon, 
  TicketIcon, 
  ClockIcon, 
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PencilIcon,
  UserGroupIcon,
  CalendarIcon,
  HeartIcon,
  CogIcon
} from '@heroicons/react/24/outline'

interface TeamMember {
  id: string
  firstName: string
  lastName: string
  email: string
  assignedTickets: number
  resolvedTickets: number
  avgResolutionTime: number
  workloadScore: number
  lastActive: string
  status: 'active' | 'away' | 'offline'
}

interface TeamPerformance {
  totalTickets: number
  resolvedTickets: number
  avgResolutionTime: number
  teamWorkload: number
}

interface ManagerDashboardStats {
  teamMembers: TeamMember[]
  teamPerformance: TeamPerformance
  recentActivity: Array<{
    id: string
    type: string
    description: string
    timestamp: string
    user: string
    ticketId?: string
  }>
  performanceTrends: Array<{
    date: string
    ticketsResolved: number
    avgResolutionTime: number
    teamEfficiency: number
  }>
  upcomingDeadlines: Array<{
    id: string
    title: string
    dueDate: string
    priority: string
    assignedTo: string
    ticketId?: string
  }>
  teamWorkload: Array<{
    userId: string
    name: string
    currentTickets: number
    maxCapacity: number
    utilization: number
  }>
}

const ManagerDashboard: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { showError, showAuthError } = useToast()
  const { handleAuthError, trackLogin } = useSessionExpiration()
  
  const [stats, setStats] = useState<ManagerDashboardStats>({
    teamMembers: [],
      teamPerformance: {
        totalTickets: 0,
        resolvedTickets: 0,
        avgResolutionTime: 0,
        teamWorkload: 0
      },
    recentActivity: [],
    performanceTrends: [],
    upcomingDeadlines: [],
    teamWorkload: []
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    trackLogin()
  }, [trackLogin])

  useEffect(() => {
    const fetchManagerDashboard = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        // Fetch manager dashboard data
        const response = await axios.get('/api/manager/dashboard')
        
        if (response.data.success) {
          setStats(response.data.data)
        } else {
          setError(response.data.error || 'Failed to fetch dashboard data')
        }
      } catch (error: any) {
        console.error('Error fetching manager dashboard:', error)
        
        // Use global error handler instead of individual error handling
        handleGlobalError(error, 'ManagerDashboard')
        setError('Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchManagerDashboard()
  }, []) // Remove dependencies to prevent infinite loops

  const formatHours = (hours: number) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`
    }
    return `${Math.round(hours)}h`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100'
      case 'away': return 'text-yellow-600 bg-yellow-100'
      case 'offline': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent': return 'text-red-600 bg-red-100'
      case 'high': return 'text-orange-600 bg-orange-100'
      case 'normal': return 'text-blue-600 bg-blue-100'
      case 'low': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const quickActions = [
    {
      name: 'View Team',
      description: 'Manage team members',
      icon: UserGroupIcon,
      action: () => navigate('/user-management'),
      color: 'bg-blue-500'
    },
    {
      name: 'Assign Tickets',
      description: 'Distribute workload',
      icon: TicketIcon,
      action: () => navigate('/tickets'),
      color: 'bg-green-500'
    },
    {
      name: 'Team Calendar',
      description: 'View team schedule',
      icon: CalendarIcon,
      action: () => navigate('/calendar'),
      color: 'bg-purple-500'
    },
    {
      name: 'Performance',
      description: 'Review team metrics',
      icon: ChartBarIcon,
      action: () => navigate('/analytics'),
      color: 'bg-indigo-500'
    }
  ]

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading dashboard</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Team Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor and manage your team's performance and workload
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => navigate('/tickets/new')}
            className="btn-primary"
          >
            Create Ticket
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <button
              key={action.name}
              onClick={action.action}
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div>
                <span className={`rounded-lg inline-flex p-3 ${action.color} text-white`}>
                  <action.icon className="h-6 w-6" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900">{action.name}</h3>
                <p className="mt-2 text-sm text-gray-500">{action.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Team Members</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.teamMembers.length}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TicketIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Active Tickets</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.teamPerformance.totalTickets}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Avg Resolution</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.teamPerformance.resolvedTickets > 0 ? formatHours(stats.teamPerformance.avgResolutionTime) : 'No resolved tickets'}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Team Workload</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.teamPerformance.teamWorkload}%
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Team Members */}
        <div className="card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members</h3>
            <div className="space-y-4">
              {stats.teamMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {member.firstName[0]}{member.lastName[0]}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{member.assignedTickets}</p>
                      <p className="text-xs text-gray-500">Tickets</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{member.workloadScore}%</p>
                      <p className="text-xs text-gray-500">Workload</p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(member.status)}`}>
                      {member.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Workload */}
        <div className="card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Team Workload</h3>
            <div className="space-y-4">
              {stats.teamWorkload.map((member) => (
                <div key={member.userId} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">{member.name}</span>
                    <span className="text-gray-500">{member.currentTickets}/{member.maxCapacity} tickets</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        member.utilization > 90 ? 'bg-red-500' :
                        member.utilization > 70 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(member.utilization, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
            {stats.recentActivity.length > 0 ? (
              <div className="flow-root">
                <ul className="-mb-8">
                  {stats.recentActivity.map((activity, activityIdx) => (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== stats.recentActivity.length - 1 ? (
                          <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                              <EyeIcon className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">{activity.description}</p>
                              <p className="text-xs text-gray-400">by {activity.user}</p>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              {formatDate(activity.timestamp)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-center py-8">
                <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
                <p className="mt-1 text-sm text-gray-500">Activity will appear here as your team works on tickets.</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Upcoming Deadlines</h3>
            {stats.upcomingDeadlines.length > 0 ? (
              <div className="space-y-3">
                {stats.upcomingDeadlines.map((deadline) => (
                  <div key={deadline.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{deadline.title}</p>
                      <p className="text-xs text-gray-500">Assigned to: {deadline.assignedTo}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(deadline.priority)}`}>
                        {deadline.priority}
                      </span>
                      <span className="text-xs text-gray-500">{formatDate(deadline.dueDate)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming deadlines</h3>
                <p className="mt-1 text-sm text-gray-500">Deadlines will appear here when tickets are scheduled for work.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ManagerDashboard
